import {
  Avatar,
  Button,
  Input,
  MediaUpload,
  Modal,
  Popover,
  showNotification,
} from 'design-system';
import { Spin, Typography } from 'design-system/compat';
import {
  ChevronRight,
  CircleAlert,
  X as CloseIcon,
  Paperclip,
  Search as SearchOutlined,
  User as UserOutlined,
} from 'lucide-react';

import useIsMobile from '@/hooks/useIsMobile';
import axios from 'axios';
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { User as UserIcon } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import CustomIcon from '../../components/icons/CustomIcon';
import '../../components/Search/search.css';
import InputForm from '../../containers/NewAuth/CreateMembership/InputForm';
import SelectDocument from '../../containers/NewDeceased/atoms/GraveClaimPage/SelectDocument/SelectDocument';
import TermsAndCondition from '../../containers/NewDeceased/atoms/GraveClaimPage/TermsAndCondition/TermsAndCondition';
import defaultBackground from '../../img/defaultGraveCoverPhoto.jpg';
import {
  makeUpdateGrave,
  setConfirmSignIn,
  setRelationDirections,
  setRelations,
  updateCurrentUserInClaimQueue,
  updateCurrentUserInRelationQueue,
} from '../../redux/actions';
import { CONSTANTS } from '../../service/constants';
import { getToken, protectedUrl, request } from '../../service/request';
import { ensureRelationsLoaded } from '../../service/relationsLoader';
import { filterRelationsByGender } from '../../service/relationGenderFilter';
import connectStyles from './ConnectWithGrave.module.css';
import styles from './SetRelationshipModal.module.css';

const { Text } = Typography;

const PRIMARY_RELATION_KEYS = ['father', 'mother', 'son', 'daughter'];

const DEFAULT_CERTIFICATE_TYPE = 'death_certificate';
const NOTIFICATION_CONTEXT = 'familyTree';

const normalizeGenderValue = (gender) => {
  if (!gender) return 'other';
  const normalized = String(gender).toLowerCase();
  if (normalized === 'mann' || normalized === 'male') return 'male';
  if (normalized === 'kvinne' || normalized === 'female') return 'female';
  return 'other';
};

const SetRelationshipModal = ({
  visible,
  onClose,
  initialGrave,
  initialRelationKey = null,
  onRelationConfirmed,
  personSearchEnabled = true,
  action = 'relation',
  onActionCompleted,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user?.user) || {};
  const isAuth = useSelector((state) => state.auth?.isAuthorized);
  const showGuestRegister = !isAuth;
  const relations = useSelector((state) => state.grave.relations);
  const relationDirections = useSelector((state) => state.grave.relationDirections);

  const currentGraveFromStore = useSelector((state) => state.grave?.grave);
  const verificationHandledRef = useRef(false);

  const baseGrave = useMemo(() => {
    if (currentGraveFromStore?.id && initialGrave?.id) {
      if (currentGraveFromStore.id === initialGrave.id) {
        return currentGraveFromStore;
      }
    }
    return initialGrave;
  }, [currentGraveFromStore, initialGrave]);

  const [activeAction, setActiveAction] = useState(action || 'relation');
  const [person, setPerson] = useState(null);
  const [relation, setRelation] = useState(null);
  const [relationDirection, setRelationDirection] = useState(null);
  const [filteredDirections, setFilteredDirections] = useState([]);
  const personFirstName = useMemo(() => {
    const name = person?.name || person?.full_name || '';
    const trimmed = name.trim();
    if (!trimmed) return '';
    return trimmed.split(/\s+/)[0] || trimmed;
  }, [person]);
  const isQuickAddMother = initialRelationKey === 'mother';
  const isQuickAddFather = initialRelationKey === 'father';
  const quickAddRoleLabel = isQuickAddMother
    ? intl.formatMessage({ id: 'family_tree_role_mother', defaultMessage: 'mother' })
    : isQuickAddFather
      ? intl.formatMessage({ id: 'family_tree_role_father', defaultMessage: 'father' })
      : intl.formatMessage({ id: 'family_tree_role_person', defaultMessage: 'person' });
  const isMobile = useIsMobile();

  const [searchValue, setSearchValue] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [perfectMatches, setPerfectMatches] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [seeMoreOpen, setSeeMoreOpen] = useState(false);
  const [relationSearchTerm, setRelationSearchTerm] = useState('');
  const relationPopoverContentRef = useRef(null);
  const relationSearchInputRef = useRef(null);
  const seeMoreButtonRef = useRef(null);
  const keepRelationPopoverOpenRef = useRef(false);
  const personSearchInputRef = useRef(null);
  const searchControlRef = useRef(null);
  const searchOverlayRef = useRef(null);
  const isUserTabbingRef = useRef(false);
  const pointerDownTargetRef = useRef(null);
  const searchValueRef = useRef('');
  const lastInitialKeyRef = useRef(null);
  const relationPrefilledRef = useRef(false);
  const [documentItems, setDocumentItems] = useState([]);
  const [selectedDocumentType, setSelectedDocumentType] = useState(DEFAULT_CERTIFICATE_TYPE);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [searchOverlayRect, setSearchOverlayRect] = useState(null);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredUser, setRegisteredUser] = useState({});
  const [isResending, setIsResending] = useState(false);
  const [editEmailOpen, setEditEmailOpen] = useState(false);
  const [editEmailValue, setEditEmailValue] = useState('');
  const [guestGender, setGuestGender] = useState(null);
  const [statusPollVersion, setStatusPollVersion] = useState(0);
  const registerSubmitRef = useRef(null);
  const returnUrl =
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : '/';

  const formatRelationLabel = useCallback(
    (rel) => {
      if (!rel) return '';
      return rel.key ? intl.formatMessage({ id: rel.key }) : rel.name;
    },
    [intl],
  );

  const activeGender = currentUser?.gender ?? guestGender;
  const normalizedActiveGender = useMemo(() => normalizeGenderValue(activeGender), [activeGender]);
  const quickAddSelfRelationKey = useMemo(() => {
    if (!initialRelationKey) return null;
    if (initialRelationKey !== 'mother' && initialRelationKey !== 'father') {
      return initialRelationKey;
    }

    if (normalizedActiveGender === 'male') return 'son';
    if (normalizedActiveGender === 'female') return 'daughter';
    return 'child';
  }, [initialRelationKey, normalizedActiveGender]);
  const genderFilteredRelations = useMemo(() => {
    return filterRelationsByGender(relations, activeGender);
  }, [relations, activeGender]);

  const relationOptions = useMemo(() => {
    const list = Array.isArray(genderFilteredRelations) ? [...genderFilteredRelations] : [];
    const existingRelationId = initialGrave?.current_user_relation?.relation?.id;
    if (existingRelationId && !list.find((item) => item.id === existingRelationId)) {
      const fallback = relations.find((item) => item.id === existingRelationId);
      if (fallback) list.push(fallback);
    }
    return list;
  }, [genderFilteredRelations, initialGrave?.current_user_relation, relations]);

  const translatedRelations = useMemo(() => {
    return relationOptions
      .map((rel) => ({
        ...rel,
        label: formatRelationLabel(rel),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, intl.locale));
  }, [relationOptions, formatRelationLabel, intl.locale]);

  const quickAddLockedRelationId = useMemo(() => {
    if (!quickAddSelfRelationKey || !translatedRelations.length) return null;
    const wanted = String(quickAddSelfRelationKey).trim().toLowerCase();
    const preset = translatedRelations.find((rel) => String(rel?.key || '').toLowerCase() === wanted);
    return preset?.id || null;
  }, [quickAddSelfRelationKey, translatedRelations]);

  useEffect(() => {
    if (!showGuestRegister) return;
    if (relation && !translatedRelations.find((rel) => rel.id === relation.id)) {
      setRelation(null);
      setRelationDirection(null);
      showNotification(
        intl.formatMessage({ id: 'relation_gender_mismatch', defaultMessage: 'Velg relasjon på nytt.' }),
        'warning',
        NOTIFICATION_CONTEXT,
      );
    }
  }, [showGuestRegister, relation, translatedRelations, intl]);

  useEffect(() => {
    if (!relationDirection) return;
    if (!filteredDirections.find((dir) => dir.id === relationDirection.id)) {
      setRelationDirection(null);
    }
  }, [relationDirection, filteredDirections]);

  const relationById = useMemo(() => {
    const map = new Map();
    relations.forEach((rel) => {
      if (rel?.id) map.set(rel.id, rel);
    });
    return map;
  }, [relations]);

  const pickPreferredReciprocalDirection = useCallback(
    (relationKey, directions) => {
      if (!Array.isArray(directions) || !directions.length) return null;

      const normalizedRelationKey = String(relationKey || '').toLowerCase();
      const fallbackDirection = directions.length === 1 ? directions[0] : null;

      if (
        (initialRelationKey === 'mother' || initialRelationKey === 'father') &&
        ['son', 'daughter', 'child'].includes(normalizedRelationKey)
      ) {
        return (
          directions.find(
            (direction) => String(direction?.key || '').toLowerCase() === String(initialRelationKey),
          ) || fallbackDirection
        );
      }

      if (!['father', 'mother'].includes(normalizedRelationKey)) {
        return fallbackDirection;
      }

      const preferredKey =
        normalizedActiveGender === 'male'
          ? 'son'
          : normalizedActiveGender === 'female'
            ? 'daughter'
            : 'child';

      return (
        directions.find((direction) => String(direction?.key || '').toLowerCase() === preferredKey) ||
        fallbackDirection
      );
    },
    [initialRelationKey, normalizedActiveGender],
  );

  useEffect(() => {
    if (!initialRelationKey) return;
    if (!relation?.key) return;
    if (!Array.isArray(filteredDirections) || !filteredDirections.length) return;

    const preferredDirection = pickPreferredReciprocalDirection(relation.key, filteredDirections);
    if (!preferredDirection?.id) return;
    if (relationDirection?.id === preferredDirection.id) return;

    setRelationDirection(preferredDirection);
  }, [
    filteredDirections,
    initialRelationKey,
    pickPreferredReciprocalDirection,
    relation?.key,
    relationDirection?.id,
  ]);

  useEffect(() => {
    if (!relation?.key) return;
    if (relation.key !== 'grandchild') return;
    if (!activeGender) return;
    const normalized = String(activeGender).toLowerCase();
    const targetKey =
      normalized === 'mann' || normalized === 'male'
        ? 'grandson'
        : normalized === 'kvinne' || normalized === 'female'
          ? 'granddaughter'
          : null;
    if (!targetKey) return;
    const target = relations.find((rel) => rel?.key === targetKey);
    if (target && target.id !== relation.id) {
      handleRelationSelect(target.id);
    }
  }, [activeGender, relation?.key, relation?.id, relations]);

  const normalizeRelationCopy = useCallback(
    (value) => {
      if (!value) return '';
      const text = String(value);
      try {
        return text.toLocaleLowerCase(intl.locale);
      } catch (error) {
        return text.toLowerCase();
      }
    },
    [intl.locale],
  );

  const buildUpdatedGrave = useCallback(
    (updates = {}, graveUser = null) => {
      if (!baseGrave) return null;

      const existingUsers = Array.isArray(baseGrave?.grave_users) ? [...baseGrave.grave_users] : [];

      if (graveUser) {
        const index = existingUsers.findIndex((item) => {
          if (!item) return false;
          if (item.id && graveUser.id && item.id === graveUser.id) return true;
          if (item.user?.id && graveUser.user?.id) {
            return item.user.id === graveUser.user.id;
          }
          return false;
        });

        if (index >= 0) {
          existingUsers[index] = graveUser;
        } else {
          existingUsers.unshift(graveUser);
        }
      }

      const result = {
        ...baseGrave,
        ...updates,
      };

      if (graveUser) {
        result.current_user_relation = graveUser;
        result.grave_users = existingUsers;
      }

      return result;
    },
    [baseGrave],
  );

  useEffect(() => {
    searchValueRef.current = searchValue;
  }, [searchValue]);

  useEffect(() => {
    if (!visible) return;
    ensureRelationsLoaded().catch((err) => console.log(err));
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    if (initialRelationKey) return;
    if (relationPrefilledRef.current) return;

    const existingRelation = initialGrave?.current_user_relation;
    if (!existingRelation?.relation?.id) return;

    const selectedRelation = translatedRelations.find(
      (rel) => rel.id === existingRelation.relation.id,
    );
    if (!selectedRelation) return;

    setRelation(selectedRelation);

    const possibleDirections = (relationDirections || [])
      .map((item) => {
        const fromId = item?.from_relation?.id || item?.from_relation_id;
        const toRelation =
          item?.to_relation ||
          (item?.to_relation_id ? relationById.get(item.to_relation_id) : null);
        if (!fromId || fromId !== selectedRelation.id) return null;
        if (!toRelation || !toRelation.id) return null;
        return { ...toRelation, label: formatRelationLabel(toRelation) };
      })
      .filter(Boolean);

    const fallbackDirection = {
      ...selectedRelation,
      label: formatRelationLabel(selectedRelation),
    };
    const normalizedDirections = possibleDirections.length
      ? possibleDirections
      : [fallbackDirection];
    setFilteredDirections(normalizedDirections);

    const matchingDirection = normalizedDirections.find(
      (dir) => dir.id === existingRelation.reciprocal_relation?.id,
    );
    if (matchingDirection) {
      setRelationDirection(matchingDirection);
    } else if (normalizedDirections.length === 1) {
      setRelationDirection(normalizedDirections[0]);
    }

    relationPrefilledRef.current = true;
  }, [
    visible,
    initialGrave?.current_user_relation,
    translatedRelations,
    relationDirections,
    formatRelationLabel,
  ]);

  const onSearch = useCallback((value) => {
    setSearchValue(value);
  }, []);

  const debouncedChangeHandler = useMemo(() => debounce(onSearch, 300), [onSearch]);

  useEffect(() => {
    return () => {
      debouncedChangeHandler.cancel();
    };
  }, [debouncedChangeHandler]);

  const resetSearchResults = useCallback(() => {
    debouncedChangeHandler.cancel();
    setSearchValue('');
    searchValueRef.current = '';
    setPerfectMatches([]);
    setSuggestions([]);
    setUsers([]);
    setIsEmpty(false);
    setLoading(false);
  }, [debouncedChangeHandler]);

  useEffect(() => {
    if (!seeMoreOpen || isMobile) return;
    const inputNode = relationSearchInputRef.current;
    if (!inputNode) return;

    inputNode.focus({ preventScroll: true });

    if (typeof relationSearchTerm === 'string' && inputNode.setSelectionRange) {
      const length = relationSearchTerm.length;
      try {
        inputNode.setSelectionRange(length, length);
      } catch (_) {
        // Some input types do not support selection range; ignore errors.
      }
    }
  }, [isMobile, seeMoreOpen, relationSearchTerm]);

  const axiosData = async (query, cancelToken) => {
    try {
      setLoading(true);
      const headers = {
        'Content-Type': 'application/json',
        Accept: '*/*',
        ...(getToken() && { Authorization: `Bearer ${getToken()}` }),
      };
      const params = {
        search: {
          query,
          tab: 'all',
          filter: { country_id: null, municipality_id: null, graveyard_id: null },
        },
        page: 1,
        per_page: 30,
      };
      const response = await axios.get(`${CONSTANTS.baseUrl}/api/graves`, {
        params,
        headers,
        cancelToken,
      });
      if (query !== searchValueRef.current) return;
      const payload = response.data || {};

      const perfectMatchList = Array.isArray(payload.perfect_matches)
        ? payload.perfect_matches
        : [];

      const suggestionCandidates = [
        Array.isArray(payload.suggestions) ? payload.suggestions : null,
        Array.isArray(payload.display_graves) ? payload.display_graves : null,
        Array.isArray(payload.graves) ? payload.graves : null,
      ];
      const suggestionList =
        suggestionCandidates.find((list) => Array.isArray(list) && list.length) || [];

      const rawUsers = Array.isArray(payload.users)
        ? payload.users
        : Array.isArray(payload.data)
          ? payload.data
          : [];
      const fetchedUsers = rawUsers.filter((user) => user.id !== currentUser?.id);

      setPerfectMatches(perfectMatchList);
      setSuggestions(suggestionList);
      setUsers(fetchedUsers);

      const hasResults =
        perfectMatchList.length > 0 || suggestionList.length > 0 || fetchedUsers.length > 0;
      setIsEmpty(!hasResults);
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error('Error fetching data:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!visible || !searchValue.trim()) {
      setPerfectMatches([]);
      setSuggestions([]);
      setUsers([]);
      return;
    }
    const source = axios.CancelToken.source();
    axiosData(searchValue, source.token);
    return () => source.cancel();
  }, [searchValue, visible]);

  useEffect(() => {
    if (!visible) return;

    setPerson(initialGrave || null);
    relationPrefilledRef.current = false;

    const nextInitialKey = initialGrave?.id || initialGrave?.slug || initialGrave?.key || null;
    if (lastInitialKeyRef.current !== nextInitialKey) {
      lastInitialKeyRef.current = nextInitialKey;
      setSearchInputValue('');
      resetSearchResults();
    }

    const canClaimFromInitial = Boolean(
      initialGrave?.can_claim && !initialGrave?.current_user_in_claim_queue,
    );
    const requestedAction = action === 'claim' ? 'claim' : 'relation';
    setActiveAction(canClaimFromInitial ? requestedAction : 'relation');
    setDocumentItems([]);
    setAcceptedTerms(false);
    setSelectedDocumentType(DEFAULT_CERTIFICATE_TYPE);
  }, [visible, initialGrave, resetSearchResults, action]);

  const focusPersonSearchInput = useCallback(() => {
    if (!visible || !personSearchEnabled) return;

    const inputRefValue = personSearchInputRef.current;
    if (!inputRefValue) return;

    const inputNode = inputRefValue.input || inputRefValue;
    if (!inputNode || typeof inputNode.focus !== 'function') return;

    if (document.activeElement !== inputNode) {
      inputNode.focus({ preventScroll: true });
    }

    if (typeof inputNode.setSelectionRange === 'function') {
      const length =
        typeof inputNode.value === 'string' ? inputNode.value.length : searchInputValue.length;
      try {
        inputNode.setSelectionRange(length, length);
      } catch (_) {
        // Some inputs do not support selection range; ignore errors.
      }
    }
  }, [personSearchEnabled, searchInputValue, visible]);

  useEffect(() => {
    focusPersonSearchInput();
  }, [focusPersonSearchInput]);

  useEffect(() => {
    if (!visible) {
      pointerDownTargetRef.current = null;
      return undefined;
    }

    const handlePointerDown = (event) => {
      pointerDownTargetRef.current = event.target;
    };

    const handlePointerUp = () => {
      pointerDownTargetRef.current = null;
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('pointerup', handlePointerUp, true);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('pointerup', handlePointerUp, true);
      pointerDownTargetRef.current = null;
    };
  }, [visible]);

  const handleSelectPerson = useCallback(
    (item) => {
      setPerson(item || null);
      setSearchInputValue('');
      resetSearchResults();
      keepRelationPopoverOpenRef.current = false;
    },
    [resetSearchResults],
  );

  const handleSearchInputChange = useCallback(
    (event) => {
      const nextValue = event.target.value;
      setSearchInputValue(nextValue);

      if (!nextValue.trim()) {
        resetSearchResults();
        requestAnimationFrame(() => {
          focusPersonSearchInput();
        });
        return;
      }

      debouncedChangeHandler(nextValue);
    },
    [debouncedChangeHandler, focusPersonSearchInput, resetSearchResults],
  );

  const handleClearSearch = useCallback(() => {
    setSearchInputValue('');
    resetSearchResults();
    requestAnimationFrame(() => {
      focusPersonSearchInput();
    });
  }, [focusPersonSearchInput, resetSearchResults]);

  const handleClearSearchMouseDown = useCallback((event) => {
    event.preventDefault();
  }, []);

  const updateSearchOverlayPosition = useCallback(() => {
    const container = searchControlRef.current;
    if (!container) {
      setSearchOverlayRect(null);
      return;
    }
    const rect = container.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || rect.width;
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight || rect.bottom;
    const horizontalPadding = 16;
    const verticalGap = 8;
    const estimatedPanelHeight = 320;

    const maxWidth = Math.max(0, viewportWidth - horizontalPadding * 2);
    const width = Math.min(rect.width, maxWidth);
    const maxLeft = viewportWidth - horizontalPadding - width;
    const left = Math.min(
      Math.max(rect.left, horizontalPadding),
      Math.max(horizontalPadding, maxLeft),
    );

    const preferTop = rect.bottom + verticalGap;
    const potentialBottom = preferTop + estimatedPanelHeight;
    const fallbackTop = rect.top - verticalGap - estimatedPanelHeight;
    const top =
      potentialBottom <= viewportHeight - verticalGap
        ? preferTop
        : Math.max(horizontalPadding, fallbackTop);

    setSearchOverlayRect({
      top,
      left,
      width,
    });
  }, []);

  const getGraveSubtitle = useCallback((item) => {
    if (!item) return '';
    const parts = [item.graveyard?.name, item.municipality?.name].flat().filter(Boolean);
    if (parts.length) return parts.join(', ');

    const cemetery = item.cemetery?.name || item.cemetery_name;
    return cemetery || '';
  }, []);

  const getUserSubtitle = useCallback((item) => {
    if (!item) return '';
    const parts = [item.city, item.country, item.location].flat().filter(Boolean);
    return parts.join(', ');
  }, []);

  const getResultLabel = useCallback(
    (item) => item?.name || item?.title || item?.full_name || item?.display_name || '',
    [],
  );

  const getResultKey = useCallback(
    (item, fallbackPrefix = 'item') => {
      if (!item) return `${fallbackPrefix}-unknown`;
      return (
        item.id ||
        item.key ||
        item.slug ||
        item.uuid ||
        `${fallbackPrefix}-${getResultLabel(item)}` ||
        `${fallbackPrefix}-${Math.random().toString(36).slice(2)}`
      );
    },
    [getResultLabel],
  );

  const renderGraveRow = useCallback(
    (item, groupPrefix, index = 0) => {
      const label = getResultLabel(item);
      if (!label) return null;
      const subtitle = getGraveSubtitle(item);
      const key = getResultKey(item, `${groupPrefix}-${index}`);

      return (
        <button
          key={key}
          type="button"
          className={`${styles.searchResultButton} hdr-search__row`}
          onClick={() => handleSelectPerson(item)}
        >
          <span className="hdr-search__thumb" aria-hidden>
            <CustomIcon name="tombstone" lib="lucide" />
          </span>
          <div className="hdr-search__meta">
            <div className="hdr-search__title">{label}</div>
            {subtitle && <div className="hdr-search__sub">{subtitle}</div>}
          </div>
          <span className="hdr-search__chev" aria-hidden>
            <ChevronRight size={16} />
          </span>
        </button>
      );
    },
    [getGraveSubtitle, getResultKey, getResultLabel, handleSelectPerson],
  );

  const renderUserRow = useCallback(
    (item, index = 0) => {
      const label = getResultLabel(item);
      if (!label) return null;
      const subtitle = getUserSubtitle(item);
      const key = getResultKey(item, `user-${index}`);

      return (
        <button
          key={key}
          type="button"
          className={`${styles.searchResultButton} hdr-search__row`}
          onClick={() => handleSelectPerson(item)}
        >
          <span className="hdr-search__thumb" aria-hidden>
            <UserOutlined size={18} />
          </span>
          <div className="hdr-search__meta">
            <div className="hdr-search__title">{label}</div>
            {subtitle && <div className="hdr-search__sub">{subtitle}</div>}
          </div>
          <span className="hdr-search__chev" aria-hidden>
            <ChevronRight size={16} />
          </span>
        </button>
      );
    },
    [getResultKey, getResultLabel, getUserSubtitle, handleSelectPerson],
  );

  const { primaryRelations, secondaryRelations } = useMemo(() => {
    if (!translatedRelations.length) {
      return { primaryRelations: [], secondaryRelations: [] };
    }

    const normalize = (value) => value?.toString().trim().toLowerCase();

    const primary = [];
    const usedIds = new Set();

    PRIMARY_RELATION_KEYS.forEach((targetKey) => {
      const normalizedTarget = targetKey.toLowerCase();
      const match = translatedRelations.find((relationItem) => {
        if (usedIds.has(relationItem.id)) return false;
        const candidates = [relationItem.key, relationItem.name, relationItem.label]
          .map(normalize)
          .filter(Boolean);
        return candidates.includes(normalizedTarget);
      });

      if (match) {
        primary.push(match);
        usedIds.add(match.id);
      }
    });

    if (primary.length < 4) {
      translatedRelations.forEach((relationItem) => {
        if (primary.length >= 4) return;
        if (usedIds.has(relationItem.id)) return;
        primary.push(relationItem);
        usedIds.add(relationItem.id);
      });
    }

    const secondary = translatedRelations.filter((relationItem) => !usedIds.has(relationItem.id));

    return {
      primaryRelations: primary,
      secondaryRelations: secondary,
    };
  }, [translatedRelations]);

  const visiblePrimaryRelations = useMemo(() => {
    if (!relation) {
      return primaryRelations;
    }

    if (primaryRelations.some((item) => item.id === relation.id)) {
      return primaryRelations;
    }

    const selectedRelation = translatedRelations.find((item) => item.id === relation.id);
    if (!selectedRelation) {
      return primaryRelations;
    }

    return [selectedRelation, ...primaryRelations.filter((item) => item.id !== relation.id)];
  }, [primaryRelations, relation, translatedRelations]);

  const filteredSecondaryRelations = useMemo(() => {
    const baseList = secondaryRelations.filter((option) => option.id !== relation?.id);
    if (!relationSearchTerm.trim()) {
      return baseList;
    }

    const query = relationSearchTerm.trim().toLowerCase();

    return baseList.filter((option) => {
      const label = option.label || formatRelationLabel(option);
      return label?.toLowerCase().includes(query);
    });
  }, [secondaryRelations, relationSearchTerm, formatRelationLabel, relation?.id]);

  const handleRelationSelect = (relationId) => {
    const selectedRelation = translatedRelations.find((rel) => rel.id === relationId);
    if (!selectedRelation) return;
    if (quickAddLockedRelationId && selectedRelation.id !== quickAddLockedRelationId) return;

    setRelation(selectedRelation);
    setSeeMoreOpen(false);
    setRelationSearchTerm('');
    keepRelationPopoverOpenRef.current = false;

    const baseDirections = relationDirections
      .filter((item) => item.from_relation.id === relationId)
      .map((item) => ({ ...item.to_relation, label: formatRelationLabel(item.to_relation) }));

    const dedupedDirections = [];
    const seenKeys = new Set();
    const seenIds = new Set();

    baseDirections.forEach((direction) => {
      if (!direction) return;
      if (direction.key) {
        if (seenKeys.has(direction.key)) return;
        seenKeys.add(direction.key);
      } else if (direction.id != null) {
        if (seenIds.has(direction.id)) return;
        seenIds.add(direction.id);
      }

      dedupedDirections.push(direction);
    });

    const normalizedDirections = dedupedDirections.length > 0 ? dedupedDirections : [];
    if (!normalizedDirections.length) {
      normalizedDirections.push({
        ...selectedRelation,
        label: formatRelationLabel(selectedRelation),
      });
    }

    setFilteredDirections(normalizedDirections);
    setRelationDirection(pickPreferredReciprocalDirection(selectedRelation.key, normalizedDirections));
  };

  const handleRelationDirectionSelect = (directionId) => {
    const selectedDirection = filteredDirections.find((dir) => dir.id === directionId);
    if (!selectedDirection) return;
    setRelationDirection(selectedDirection);
  };

  useEffect(() => {
    if (!visible) return;
    if (!quickAddSelfRelationKey) return;
    if (!translatedRelations.length) return;

    const wanted = String(quickAddSelfRelationKey).trim().toLowerCase();
    const preset = translatedRelations.find((rel) => String(rel?.key || '').toLowerCase() === wanted);
    if (!preset?.id) return;
    if (relation?.id === preset.id) return;
    handleRelationSelect(preset.id);
  }, [visible, quickAddSelfRelationKey, relation, translatedRelations]);

  const handleDiscard = () => {
    setPerson(null);
    setRelation(null);
    setRelationDirection(null);
    setFilteredDirections([]);
    setSearchValue('');
    setSearchInputValue('');
    searchValueRef.current = '';
    setPerfectMatches([]);
    setSuggestions([]);
    setUsers([]);
    setIsEmpty(false);
    setSeeMoreOpen(false);
    setRelationSearchTerm('');
    keepRelationPopoverOpenRef.current = false;
    setDocumentItems([]);
    setAcceptedTerms(false);
    setSelectedDocumentType(DEFAULT_CERTIFICATE_TYPE);
    setActiveAction('relation');
    onClose();
  };

  useEffect(() => {
    if (!seeMoreOpen) return undefined;

    const handlePointerDown = (event) => {
      const popoverEl = relationPopoverContentRef.current;
      const buttonEl = seeMoreButtonRef.current;
      if (popoverEl?.contains(event.target) || buttonEl?.contains(event.target)) {
        return;
      }
      setSeeMoreOpen(false);
      setRelationSearchTerm('');
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSeeMoreOpen(false);
        setRelationSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [seeMoreOpen]);

  const handleCancel = () => {
    const hasClaimDraft = documentItems.length > 0 || acceptedTerms;
    const hasUnsavedChanges =
      relation || relationDirection || hasClaimDraft || activeAction === 'claim';

    if (hasUnsavedChanges) {
      Modal.confirm({
        icon: <CircleAlert size={24} aria-hidden />,
        title: intl.formatMessage({ id: 'discard_changes' }),
        content: intl.formatMessage({ id: 'memory_discard_message' }),
        okText: intl.formatMessage({ id: 'discard' }),
        cancelText: intl.formatMessage({ id: 'discard_cancel' }),
        okType: 'danger',
        onOk: handleDiscard,
      });
    } else {
      handleDiscard();
    }
  };

  const selectedGraveId = person?.full_name ? null : person?.id;
  const baseGraveId = baseGrave?.id;
  const isInitialGraveSelected =
    selectedGraveId && baseGraveId && String(selectedGraveId) === String(baseGraveId);
  const claimAvailable = Boolean(
    isInitialGraveSelected && baseGrave?.can_claim && !baseGrave?.current_user_in_claim_queue,
  );
  const claimRequirementsMet = Boolean(
    documentItems[0]?.file && acceptedTerms && selectedDocumentType,
  );
  const showActionToggle = Boolean(claimAvailable || activeAction === 'claim');

  const handleSave = async () => {
    if (!person || !relation || !relationDirection) return;

    if (showGuestRegister) {
      if (registrationComplete) return;
      if (typeof registerSubmitRef.current === 'function') {
        registerSubmitRef.current();
      }
      return;
    }

    if (person?.full_name) {
      setIsSubmitting(true);
      try {
        const payload = {
          slug: person.slug,
          relation_id: relation.id,
          reciprocal_relation_id: relationDirection.id,
        };
        const response = await request('/user_connections', payload, 'post');
        const data = response.data;
        if (data?.status === 'accepted' && onRelationConfirmed) {
          onRelationConfirmed(person.id, data);
        } else {
          dispatch(updateCurrentUserInRelationQueue());
        }
        handleDiscard();
      } catch (error) {
        if (error.response?.data?.success === false) {
          showNotification(
            intl.formatMessage({ id: 'user_has_already_been_taken' }),
            'error',
            NOTIFICATION_CONTEXT,
          );
        } else {
          showNotification(
            error.response?.data?.error || error.message || 'Error',
            'error',
            NOTIFICATION_CONTEXT,
          );
        }
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    const isClaimFlow = activeAction === 'claim' && claimAvailable;

    if (isClaimFlow && !claimRequirementsMet) {
      showNotification(
        intl.formatMessage({ id: 'please_fill_all_fields' }),
        'warning',
        NOTIFICATION_CONTEXT,
      );
      return;
    }

    const graveId = person.id;

    setIsSubmitting(true);
    try {
      if (isClaimFlow) {
        const formData = new FormData();
        formData.append('death_certificate[grave_id]', graveId);
        formData.append(
          'death_certificate[certificate_type]',
          selectedDocumentType || DEFAULT_CERTIFICATE_TYPE,
        );
        formData.append('death_certificate[image]', documentItems[0].file);
        formData.append('relation_id', relation.id);
        formData.append('reciprocal_relation_id', relationDirection.id);

        const response = await request('/grave_claims', formData, 'post', {
          'Content-Type': 'multipart/form-data',
        });
        const graveUser = response.data;
        const status = graveUser?.status;

        dispatch(updateCurrentUserInClaimQueue());

        const updatedGrave = buildUpdatedGrave(
          {
            can_claim: false,
            current_user_in_claim_queue: true,
            can_set_relations: false,
          },
          graveUser,
        );

        if (updatedGrave && currentGraveFromStore?.id === updatedGrave?.id) {
          dispatch(makeUpdateGrave(updatedGrave));
        }

        if (onRelationConfirmed) {
          onRelationConfirmed(graveId, graveUser);
        }

        if (onActionCompleted) {
          onActionCompleted({ action: 'claim', status, graveUser, grave: updatedGrave });
        }

        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
          try {
            window.dispatchEvent(
              new CustomEvent('cache-invalidate', {
                detail: {
                  payload: { resource: '/api/graves_joined_by_users' },
                  keys: ['/api/graves_joined_by_users'],
                  reason: 'grave-claim-submitted',
                  at: Date.now(),
                },
              }),
            );
          } catch {}
        }

        handleDiscard();
        return;
      }

      const payload = {
        grave_user: {
          grave_id: graveId,
          relation_id: relation.id,
          reciprocal_relation_id: relationDirection.id,
        },
      };

      const response = await request('/grave_relations', payload, 'post');
      const graveUser = response.data;
      const status = graveUser?.status;

      if (status === 'accepted') {
        const updatedGrave = buildUpdatedGrave(
          {
            can_set_relations: false,
            current_user_in_set_relation_queue: false,
          },
          graveUser,
        );

        if (updatedGrave && currentGraveFromStore?.id === updatedGrave?.id) {
          dispatch(makeUpdateGrave(updatedGrave));
        }

        if (onActionCompleted) {
          onActionCompleted({ action: 'relation', status, graveUser, grave: updatedGrave });
        }
      } else {
        dispatch(updateCurrentUserInRelationQueue());
        if (onActionCompleted) {
          onActionCompleted({ action: 'relation', status, graveUser, grave: baseGrave });
        }
      }

      if (onRelationConfirmed) {
        onRelationConfirmed(graveId, graveUser);
      }

      handleDiscard();
    } catch (error) {
      const responseData = error.response?.data;
      const serverMessage =
        responseData?.message ||
        (Array.isArray(responseData?.errors) && responseData.errors[0]) ||
        responseData?.error;

      if (serverMessage) {
        showNotification(serverMessage, 'error', NOTIFICATION_CONTEXT);
      } else if (!isClaimFlow && responseData?.success === false) {
        showNotification(
          intl.formatMessage({ id: 'relation_already_exists' }),
          'error',
          NOTIFICATION_CONTEXT,
        );
      } else {
        showNotification(error.message || 'Error', 'error', NOTIFICATION_CONTEXT);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const relationLabelText = relation ? formatRelationLabel(relation) : '';
  const reciprocalRelationLabelText = relationDirection
    ? formatRelationLabel(relationDirection)
    : '';
  const relationLabelLower = relationLabelText && normalizeRelationCopy(relationLabelText);
  const reciprocalRelationLabelLower =
    reciprocalRelationLabelText && normalizeRelationCopy(reciprocalRelationLabelText);
  const summaryPersonName = person?.name || person?.full_name || baseGrave?.name || '';

  const canSubmitRelation = Boolean(person && relation && relationDirection);
  const canSubmitClaim = Boolean(canSubmitRelation && claimRequirementsMet && claimAvailable);
  const canSave = (() => {
    if (activeAction === 'claim') {
      return isAuth && canSubmitClaim;
    }

    if (showGuestRegister) {
      return canSubmitRelation && !registrationComplete && !isRegistering;
    }

    return canSubmitRelation;
  })();

  const handleClaimDocumentTypeChange = useCallback(
    (value) => {
      const nextType = value || null;
      setSelectedDocumentType(nextType);

      if (nextType !== selectedDocumentType) {
        setDocumentItems([]);
        setAcceptedTerms(false);
      }
    },
    [selectedDocumentType],
  );

  useEffect(() => {
    if (!visible) return;
    if (activeAction !== 'claim') return;
    if (claimAvailable) return;
    setActiveAction('relation');
  }, [visible, activeAction, claimAvailable]);

  useEffect(() => {
    if (!visible) return;
    if (!action) return;
    setActiveAction(action);
  }, [visible, action]);

  useEffect(() => {
    if (activeAction === 'claim') return;
    setDocumentItems([]);
    setAcceptedTerms(false);
    setSelectedDocumentType(DEFAULT_CERTIFICATE_TYPE);
  }, [activeAction]);

  const selectedId = String(person?.id || person?.key);

  const filteredPerfectMatches = person?.full_name
    ? perfectMatches
    : perfectMatches.filter((item) => String(item.id || item.key) !== selectedId);
  const filteredSuggestions = person?.full_name
    ? suggestions
    : suggestions.filter((item) => String(item.id || item.key) !== selectedId);
  const filteredUsers = person?.full_name
    ? users.filter((item) => String(item.id) !== selectedId)
    : users;

  const hasSearchResults =
    filteredPerfectMatches.length > 0 || filteredSuggestions.length > 0 || filteredUsers.length > 0;

  const shouldShowLoadingOverlay =
    visible && personSearchEnabled && searchInputValue.trim() && loading;

  const shouldShowResultsOverlay =
    visible && personSearchEnabled && !loading && searchValue.trim() && hasSearchResults;

  const shouldShowOverlay = shouldShowLoadingOverlay || shouldShowResultsOverlay;

  useLayoutEffect(() => {
    if (!shouldShowOverlay) {
      setSearchOverlayRect(null);
      return;
    }
    updateSearchOverlayPosition();
  }, [
    shouldShowOverlay,
    updateSearchOverlayPosition,
    searchInputValue,
    searchValue,
    filteredPerfectMatches.length,
    filteredSuggestions.length,
    filteredUsers.length,
  ]);

  useEffect(() => {
    if (!shouldShowOverlay) return undefined;

    const handleReposition = () => {
      updateSearchOverlayPosition();
    };

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [shouldShowOverlay, updateSearchOverlayPosition]);

  const overlayStyle = searchOverlayRect
    ? {
        top: searchOverlayRect.top,
        left: searchOverlayRect.left,
        width: searchOverlayRect.width,
      }
    : null;

  const overlayContent = (() => {
    if (!overlayStyle) return null;
    if (shouldShowLoadingOverlay) {
      return (
        <div ref={searchOverlayRef} className={styles.searchLoading} style={overlayStyle}>
          <Spin size="small" />
        </div>
      );
    }
    if (shouldShowResultsOverlay) {
      return (
        <div ref={searchOverlayRef} className={styles.searchResults} style={overlayStyle}>
          <div className={`hdr-search__body ${styles.searchResultsBody}`}>
            {filteredPerfectMatches.length > 0 && (
              <div className={styles.searchSection}>
                <div className="hdr-search__section-hd">
                  <FormattedMessage id="search_grave_perfect_matches" />
                </div>
                <div className="hdr-search__rows">
                  {filteredPerfectMatches.map((item, index) =>
                    renderGraveRow(item, 'perfect', index),
                  )}
                </div>
              </div>
            )}

            {filteredSuggestions.length > 0 && (
              <div className={styles.searchSection}>
                <div className="hdr-search__section-hd">
                  <FormattedMessage
                    id={
                      filteredSuggestions.length === 1 ? 'results_count_one' : 'results_count_other'
                    }
                    values={{ count: filteredSuggestions.length }}
                  />
                </div>
                <div className="hdr-search__rows">
                  {filteredSuggestions.map((item, index) =>
                    renderGraveRow(item, 'suggestion', index),
                  )}
                </div>
              </div>
            )}

            {filteredUsers.length > 0 && (
              <div className={styles.searchSection}>
                <div className="hdr-search__section-hd">
                  <FormattedMessage id="search_tab_members" />
                </div>
                <div className="hdr-search__rows">
                  {filteredUsers.map((item, index) => renderUserRow(item, index))}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  })();

  const searchOverlayPortal =
    shouldShowOverlay && overlayContent && typeof document !== 'undefined'
      ? createPortal(overlayContent, document.body)
      : null;

  const handleSearchInputBlur = useCallback(
    (event) => {
      if (!visible || !personSearchEnabled) return;

      const hasActiveSearch = Boolean(searchInputValue.trim()) || shouldShowOverlay;
      if (!hasActiveSearch) return;

      if (isUserTabbingRef.current) {
        isUserTabbingRef.current = false;
        return;
      }

      const nextFocusTarget =
        typeof Node !== 'undefined' && event.relatedTarget instanceof Node
          ? event.relatedTarget
          : null;
      const overlayNode = searchOverlayRef.current;
      const containerNode = searchControlRef.current;
      const pointerTarget = pointerDownTargetRef.current;

      pointerDownTargetRef.current = null;

      if (
        nextFocusTarget &&
        (containerNode?.contains(nextFocusTarget) || overlayNode?.contains(nextFocusTarget))
      ) {
        return;
      }

      if (typeof Node !== 'undefined' && pointerTarget instanceof Node) {
        if (
          (nextFocusTarget &&
            (nextFocusTarget.contains(pointerTarget) || pointerTarget.contains(nextFocusTarget))) ||
          containerNode?.contains(pointerTarget) ||
          overlayNode?.contains(pointerTarget)
        ) {
          return;
        }
      }

      requestAnimationFrame(() => {
        focusPersonSearchInput();
      });
    },
    [focusPersonSearchInput, personSearchEnabled, searchInputValue, shouldShowOverlay, visible],
  );

  const handleSearchInputKeyDown = useCallback((event) => {
    if (event.key === 'Tab') {
      isUserTabbingRef.current = true;
    }
  }, []);

  const handleSearchInputKeyUp = useCallback((event) => {
    if (event.key === 'Tab') {
      isUserTabbingRef.current = false;
    }
  }, []);

  const relationButtonSize = 'small';

  const userAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const parsed = dayjs(dateOfBirth, 'YYYY-MM-DD', true).isValid()
      ? dayjs(dateOfBirth, 'YYYY-MM-DD', true)
      : dayjs(dateOfBirth);
    if (!parsed.isValid()) return null;
    return dayjs().diff(parsed, 'year');
  };

  const resolvedPersonName = person?.name || person?.full_name || personFirstName;
  const resolvedPersonFirstName =
    personFirstName ||
    (resolvedPersonName ? resolvedPersonName.split(/\s+/)[0] : '') ||
    intl.formatMessage({
      id: 'relation_summary_fallback_person',
      defaultMessage: 'this person',
    });

  const guestDefaults = useMemo(() => {
    if (typeof window === 'undefined') return { country: null, language: null };
    const host = window.location.hostname || '';
    const map = [
      { match: ['nettgrav.no'], country: 'NO', language: 'NOR' },
      { match: ['natgrav.se'], country: 'SE', language: 'SWE' },
      { match: ['netgrav.dk'], country: 'DK', language: 'DAN' },
      { match: ['verkkohauta.fi'], country: 'FI', language: 'FIN' },
      { match: ['nettgrav.com'], country: 'US', language: 'ENG' },
    ];
    const hit = map.find((entry) => entry.match.some((m) => host.includes(m)));
    return {
      country: hit?.country || null,
      language: hit?.language || null,
    };
  }, []);

  const baseGraveSlug = baseGrave?.slug;

  useEffect(() => {
    if (!visible) {
      verificationHandledRef.current = false;
    }
  }, [visible]);

  useEffect(() => {
    if (!showGuestRegister) return;
    if (!registrationComplete) return;
    if (!baseGrave?.id || !relation?.id || !relationDirection?.id) return;
    try {
      const returnUrl = (() => {
        if (typeof window === 'undefined') return '';
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('intent');
          return `${url.pathname}${url.search}`;
        } catch {
          return `${window.location.pathname}${window.location.search}`;
        }
      })();
      const payload = {
        grave_id: baseGrave.id,
        relation_id: relation.id,
        reciprocal_relation_id: relationDirection.id,
        return_url: returnUrl,
      };
      localStorage.setItem('ng.pending_grave_relation', JSON.stringify(payload));
    } catch {}
  }, [
    showGuestRegister,
    registrationComplete,
    baseGrave?.id,
    relation?.id,
    relationDirection?.id,
  ]);

  const runVerificationCheck = useCallback(() => {
    const authed = localStorage.getItem('auth') === 'true' && localStorage.getItem('token');
    const pending = localStorage.getItem('ng.pending_grave_relation');
    if (authed && !pending && !verificationHandledRef.current) {
      verificationHandledRef.current = true;
      try {
        dispatch(setConfirmSignIn());
      } catch {}
      setTimeout(() => {
        try {
          if (baseGraveSlug && typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('cache-invalidate', {
                detail: { resource: `/api/graves/${baseGraveSlug}` },
              }),
            );
          }
        } catch {}
        try {
          handleCancel();
        } catch {}
      }, 500);
    }
  }, [dispatch, baseGraveSlug, handleCancel]);

  useEffect(() => {
    if (!showGuestRegister || !registrationComplete || !visible) return;

    let isActive = true;

    const handleStorage = (event) => {
      if (!event?.key) return;
      if (['auth', 'token', 'ng.pending_grave_relation'].includes(event.key)) {
        runVerificationCheck();
      }
    };

    runVerificationCheck();
    window.addEventListener('storage', handleStorage);
    const interval = window.setInterval(() => {
      if (!isActive) return;
      runVerificationCheck();
    }, 2000);

    return () => {
      isActive = false;
      window.removeEventListener('storage', handleStorage);
      window.clearInterval(interval);
    };
  }, [showGuestRegister, registrationComplete, visible, runVerificationCheck]);

  useEffect(() => {
    if (registrationComplete) {
      setEditEmailValue(registeredUser?.email || '');
    }
  }, [registrationComplete, registeredUser?.email]);

  useEffect(() => {
    if (!registrationComplete) return undefined;
    const status = registeredUser?.confirmation_email_status;
    if (status !== 'pending' && status !== 'sending') return undefined;

    const timer = window.setTimeout(() => {
      setRegisteredUser((prev) => {
        const currentStatus = prev?.confirmation_email_status;
        if (currentStatus !== 'pending' && currentStatus !== 'sending') return prev;
        return {
          ...prev,
          confirmation_email_status: 'sent',
          confirmation_email_error_code: null,
          confirmation_email_error_message: null,
        };
      });
    }, 1500);

    return () => window.clearTimeout(timer);
  }, [registrationComplete, registeredUser?.confirmation_email_status]);

  useEffect(() => {
    if (!registrationComplete) return undefined;
    const status = registeredUser?.confirmation_email_status;
    const shouldPoll = ['pending', 'sending', 'sent'].includes(status);
    if (!shouldPoll || !registeredUser?.email) return undefined;

    let cancelled = false;
    let elapsedMs = 0;
    const intervalMs = 2000;
    const maxMs = 20000;

    const pollStatus = async () => {
      try {
        const res = await request(
          '/account_confirmations/status',
          {user: {user_id: registeredUser?.id, email: registeredUser.email}},
          'post',
        );
        if (cancelled) return;

        const nextStatus = res?.data?.confirmation_email_status;
        if (!nextStatus) return;

        setRegisteredUser((prev) => ({
          ...prev,
          confirmation_email_status: nextStatus,
          confirmation_email_error_code: res?.data?.confirmation_email_error_code || null,
          confirmation_email_error_message: res?.data?.confirmation_email_error_message || null,
          confirmation_email_sent_at: res?.data?.confirmation_email_sent_at || prev?.confirmation_email_sent_at,
          confirmation_email_last_event_at:
            res?.data?.confirmation_email_last_event_at || prev?.confirmation_email_last_event_at,
        }));
      } catch {
      } finally {
        elapsedMs += intervalMs;
      }
    };

    const timer = window.setInterval(() => {
      if (cancelled || elapsedMs >= maxMs) return;
      pollStatus();
    }, intervalMs);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [
    registrationComplete,
    registeredUser?.confirmation_email_status,
    registeredUser?.email,
    registeredUser?.id,
    statusPollVersion,
  ]);

  const handleResendConfirmation = async () => {
    if (!registeredUser?.email) return;
    setIsResending(true);
    try {
      await request(
        '/account_confirmations/resend_account_confirmation_email',
        { user: { email: registeredUser.email } },
        'post',
      );
      setRegisteredUser((prev) => ({
        ...prev,
        confirmation_email_status: 'sent',
        confirmation_email_error_code: null,
        confirmation_email_error_message: null,
      }));
      setStatusPollVersion((v) => v + 1);
      showNotification(
        intl.formatMessage({ id: 'signup_resend_toast' }),
        'success',
        NOTIFICATION_CONTEXT,
      );
    } catch (error) {
      showNotification(
        error.response?.data?.error || error.message || 'Error',
        'error',
        NOTIFICATION_CONTEXT,
      );
    } finally {
      setIsResending(false);
    }
  };

  const handleUpdateEmail = async () => {
    if (!editEmailValue?.trim() || !registeredUser?.id) return;
    setIsResending(true);
    try {
      await request(
        '/account_confirmations/resend_account_confirmation_email',
        { user: { user_id: registeredUser.id, email: editEmailValue.trim() } },
        'post',
      );
      setRegisteredUser((prev) => ({
        ...prev,
        email: editEmailValue.trim(),
        confirmation_email_status: 'sent',
        confirmation_email_error_code: null,
        confirmation_email_error_message: null,
      }));
      setStatusPollVersion((v) => v + 1);
      setEditEmailOpen(false);
      showNotification(
        intl.formatMessage({ id: 'signup_email_updated' }),
        'success',
        NOTIFICATION_CONTEXT,
      );
    } catch (error) {
      showNotification(
        error.response?.data?.error || error.message || 'Error',
        'error',
        NOTIFICATION_CONTEXT,
      );
    } finally {
      setIsResending(false);
    }
  };

  return (
    <>
      {searchOverlayPortal}
      <Modal
        title={
          activeAction === 'claim' ? (
            <FormattedMessage
              id="family_tree_modal_claim_title"
              defaultMessage="Care responsibility for the grave"
            />
          ) : isQuickAddMother ? (
            <FormattedMessage
              id="family_tree_modal_add_mother_title"
              defaultMessage="Add your mother"
            />
          ) : isQuickAddFather ? (
            <FormattedMessage
              id="family_tree_modal_add_father_title"
              defaultMessage="Add your father"
            />
          ) : !personSearchEnabled && (resolvedPersonName || resolvedPersonFirstName) ? (
            <FormattedMessage
              id="family_tree_modal_suggestion_title"
              defaultMessage="Choose your relation to {name}"
              values={{ name: resolvedPersonName || resolvedPersonFirstName }}
            />
          ) : (
            <FormattedMessage
              id="family_tree_modal_title"
              defaultMessage="Set Relationship"
              values={{ name: resolvedPersonName || resolvedPersonFirstName }}
            />
          )
        }
        open={visible}
        onCancel={handleCancel}
        cancelText={<FormattedMessage id="settings_cancel" defaultMessage="Cancel" />}
        okText={
          activeAction === 'claim' ? (
            <FormattedMessage
              id="family_tree_modal_confirm_care"
              defaultMessage="Confirm care responsibility"
            />
          ) : showGuestRegister && registrationComplete ? (
            <FormattedMessage
              id="signup_waiting_cta"
              defaultMessage="Awaiting confirmation"
            />
          ) : (
            <FormattedMessage
              id="family_tree_modal_confirm_relation"
              defaultMessage="Connect the relationship"
            />
          )
        }
        onOk={handleSave}
        fullscreen="mobile"
        style={{ width: isMobile ? '100%' : 700 }}
        okButtonProps={{ disabled: !canSave, loading: isSubmitting || isRegistering }}
        cancelButtonProps={{
          disabled:
            isSubmitting ||
            isRegistering ||
            (showGuestRegister && registrationComplete),
        }}
        maskClosable={!(showGuestRegister && registrationComplete)}
        closeOnOutsideClick={!(showGuestRegister && registrationComplete)}
      >
        {showActionToggle && (
          <div className={styles.actionToggle}>
            <Button
              size={!isMobile ? 'middle' : 'small'}
              type={activeAction === 'relation' ? 'primary' : 'default'}
              onClick={() => setActiveAction('relation')}
              disabled={isSubmitting || (showGuestRegister && registrationComplete)}
            >
              <FormattedMessage id="family_tree_modal_tab_relation" defaultMessage="Relationship" />
            </Button>
            <Button
              size={!isMobile ? 'middle' : 'small'}
              type={activeAction === 'claim' ? 'primary' : 'default'}
              onClick={() => claimAvailable && setActiveAction('claim')}
              disabled={!claimAvailable || isSubmitting || (showGuestRegister && registrationComplete)}
            >
              <FormattedMessage
                id="family_tree_modal_tab_care"
                defaultMessage="Care responsibility"
              />
            </Button>
          </div>
        )}

        <div className={showGuestRegister ? styles.splitLayout : undefined}>
          <div className={showGuestRegister ? styles.splitMain : undefined}>
        {person && (
          <div className={connectStyles.graveLinkContainer}>
            <div className={connectStyles.graveContainer}>
              <div className={connectStyles.graveWrapper}>
                <div className={connectStyles.graveAvatar}>
                  <Avatar
                    src={currentUser?.avatar ? protectedUrl(currentUser.avatar) : null}
                    shape="circle"
                    style={{
                      borderRadius: '999px',
                      height: '100%',
                      width: '100%',
                      color: 'var(--text-subtle)',
                    }}
                    icon={<UserIcon size={48} />}
                  />
                </div>
                <div className={connectStyles.graveName}>
                  <FormattedMessage id="settings_you" defaultMessage="You" />
                </div>
                <div className={connectStyles.graveDates}>
                  {currentUser.date_of_birth && (
                    <Text type="secondary">{userAge(currentUser.date_of_birth)}</Text>
                  )}
                </div>
                {!isMobile && (
                  <div
                    className={connectStyles.graveRelation}
                    style={{ marginBottom: 8, visibility: relation ? 'visible' : 'hidden' }}
                  >
                    {relation ? (relation.key ? <FormattedMessage id={relation.key} /> : relation.name) : '—'}
                  </div>
                )}
                {isMobile && (
                  <div
                    className={connectStyles.graveRelation}
                    style={{ marginBottom: 8, visibility: relation ? 'visible' : 'hidden' }}
                  >
                    {relation ? (relation.key ? <FormattedMessage id={relation.key} /> : relation.name) : '—'}
                  </div>
                )}
              </div>
            </div>
            <div className={connectStyles.graveContainer}>
              <div className={connectStyles.graveWrapper}>
                <div className={connectStyles.graveAvatar}>
                  <Avatar
                    src={person?.avatar}
                    style={{ borderRadius: '999px', height: '100%', width: '100%' }}
                    shape="circle"
                    icon={
                      person?.name ? (
                        <img
                          src={defaultBackground}
                          alt={intl.formatMessage({ id: 'family_tree_tab' })}
                        />
                      ) : (
                        <UserIcon size={48} />
                      )
                    }
                  />
                </div>
                <div className={connectStyles.graveName}>{person?.name || person?.full_name}</div>
                <div className={connectStyles.graveDates}>
                  {(person?.date_of_birth || person?.date_of_death) && (
                    <Text type="secondary">
                      {person?.date_of_birth && person?.date_of_death
                        ? `${person.date_of_birth.substring(0, 4)} - ${person.date_of_death.substring(0, 4)}`
                        : person?.date_of_birth
                          ? person.date_of_birth.substring(0, 4)
                          : person?.date_of_death
                            ? person.date_of_death.substring(0, 4)
                            : ''}
                    </Text>
                  )}
                </div>
                {!isMobile && (
                  <div
                    className={connectStyles.graveRelation}
                    style={{ marginBottom: 8, visibility: relationDirection ? 'visible' : 'hidden' }}
                  >
                    {relationDirection ? (
                      relationDirection.key ? (
                        <FormattedMessage id={relationDirection.key} />
                      ) : (
                        relationDirection.name
                      )
                    ) : (
                      '—'
                    )}
                  </div>
                )}
                {isMobile && (
                  <div
                    className={connectStyles.graveRelation}
                    style={{ marginBottom: 8, visibility: relationDirection ? 'visible' : 'hidden' }}
                  >
                    {relationDirection ? (
                      relationDirection.key ? (
                        <FormattedMessage id={relationDirection.key} />
                      ) : (
                        relationDirection.name
                      )
                    ) : (
                      '—'
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {personSearchEnabled && (
          <div className={styles.field}>
            <label className={styles.label}>
              {initialRelationKey ? (
                <FormattedMessage
                  id="family_tree_modal_search_person_label"
                  defaultMessage="Search and select person"
                />
              ) : (
                <FormattedMessage
                  id="family_tree_modal_select_grave_label"
                  defaultMessage="Type and Select grave/life page"
                />
              )}
            </label>
            <div className={styles.searchControl} ref={searchControlRef}>
              <div className={styles.searchInputWrapper}>
                <SearchOutlined size={16} className={styles.searchInputIcon} aria-hidden />
                <Input
                  ref={personSearchInputRef}
                  value={searchInputValue}
                  onChange={handleSearchInputChange}
                  onBlur={handleSearchInputBlur}
                  onKeyDown={handleSearchInputKeyDown}
                  onKeyUp={handleSearchInputKeyUp}
                  className={styles.searchInput}
                  placeholder={
                    initialRelationKey
                      ? intl.formatMessage(
                        {
                          id: 'family_tree_modal_search_person_placeholder',
                          defaultMessage: 'Search for your {role}',
                        },
                        {role: quickAddRoleLabel},
                      )
                      : intl.formatMessage({
                          id: 'family_tree_modal_select_grave_placeholder',
                          defaultMessage: 'Type and select a name',
                        })
                  }
                  aria-label={
                    initialRelationKey
                      ? intl.formatMessage({
                          id: 'family_tree_modal_search_person_label',
                          defaultMessage: 'Search and select person',
                        })
                      : intl.formatMessage({
                          id: 'family_tree_modal_select_grave_label',
                          defaultMessage: 'Type and Select grave/life page',
                        })
                  }
                />
                {searchInputValue && (
                  <button
                    type="button"
                    className={styles.searchClearButton}
                    onClick={handleClearSearch}
                    onMouseDown={handleClearSearchMouseDown}
                    aria-label={intl.formatMessage({
                      id: 'clear_search',
                      defaultMessage: 'Clear search',
                    })}
                  >
                    <CloseIcon size={14} aria-hidden />
                  </button>
                )}
              </div>
            </div>

            {initialRelationKey && (
              <div className={styles.inviteHint}>
                <FormattedMessage
                  id="family_tree_modal_search_person_hint"
                  defaultMessage="Type at least 2 letters to search by name."
                />
              </div>
            )}

            {isEmpty && !loading && searchValue.trim() && (
              <div className={styles.inviteHint}>
                <FormattedMessage
                  id="family_tree_modal_invite_person"
                  defaultMessage="No matches yet. Try full name or alternate spelling."
                />
              </div>
            )}
          </div>
        )}
        {person && initialRelationKey && (
        <div className={styles.field}>
          <label className={styles.label}>
            <FormattedMessage
              id="family_tree_modal_locked_relation_label"
              defaultMessage="Relationship"
            />
          </label>
          <div className={styles.relationSummaryCard}>
            <div className={styles.relationSummaryLine}>
              <FormattedMessage
                id="family_tree_modal_locked_relation_value"
                defaultMessage="Saved as: {relation}"
                values={{
                  relation: relationDirection
                    ? (relationDirection.key ? intl.formatMessage({ id: relationDirection.key }) : relationDirection.name)
                    : quickAddRoleLabel,
                }}
              />
            </div>
          </div>
        </div>
        )}
        {person && !initialRelationKey && (
        <div className={styles.field}>
          <label className={styles.label}>
            <FormattedMessage
              id="family_tree_modal_relation_to_person_label"
              defaultMessage="What is your relationship to this person?"
              values={{ name: resolvedPersonFirstName }}
            />
          </label>
          {(relation || relationDirection) && (
            <div className={styles.relationSummaryCard}>
              {relation && (
                <div className={styles.relationSummaryLine}>
                  {intl.formatMessage(
                    {
                      id: 'relation_summary_self',
                      defaultMessage: 'I am {relationLower}',
                    },
                    {
                      name: resolvedPersonFirstName,
                      relation: relationLabelText,
                      relationLower: relationLabelLower || relationLabelText,
                    },
                  )}
                </div>
              )}
              {relationDirection && (
                <div className={styles.relationSummaryLine}>
                  {intl.formatMessage(
                    {
                      id: 'relation_summary_target',
                      defaultMessage: '{name} is my {relationLower}',
                    },
                    {
                      name:
                        summaryPersonName ||
                        intl.formatMessage({
                          id: 'relation_summary_fallback_person',
                          defaultMessage: 'this person',
                        }),
                      relation: reciprocalRelationLabelText,
                      relationLower: reciprocalRelationLabelLower || reciprocalRelationLabelText,
                    },
                  )}
                </div>
              )}
            </div>
          )}
          <div className={styles.relationOptionList}>
            {visiblePrimaryRelations.map((option) => {
              const isSelected = relation?.id === option.id;
              const isLockedQuickAddOption =
                Boolean(quickAddLockedRelationId) && option.id !== quickAddLockedRelationId;
              return (
                <Button
                  key={option.id}
                  className={styles.relationChip}
                  shape="round"
                  size={relationButtonSize}
                  type={isSelected ? 'primary' : 'default'}
                  disabled={
                    (showGuestRegister && registrationComplete) ||
                    isLockedQuickAddOption
                  }
                  onClick={() => handleRelationSelect(option.id)}
                >
                  {option.label}
                </Button>
              );
            })}
            {!!secondaryRelations.length && !quickAddLockedRelationId && (
              <Popover
                content={
                  <div
                    ref={relationPopoverContentRef}
                    className={styles.relationPopover}
                    onMouseDownCapture={() => {
                      keepRelationPopoverOpenRef.current = true;
                    }}
                  >
                    <div className={styles.relationPopoverSearch}>
                      <Input
                        allowClear
                        size="small"
                        ref={relationSearchInputRef}
                        value={relationSearchTerm}
                        onChange={(event) => setRelationSearchTerm(event.target.value)}
                        placeholder={intl.formatMessage({
                          id: 'family_tree_modal_relation_search_placeholder',
                          defaultMessage: 'Search relations',
                        })}
                      />
                    </div>
                    <div className={styles.relationPopoverList}>
                      {filteredSecondaryRelations.length ? (
                        filteredSecondaryRelations.map((option) => {
                          const isSelected = relation?.id === option.id;
                          return (
                            <Button
                              key={option.id}
                              block
                              className={styles.relationPopoverButton}
                              size={relationButtonSize}
                              type={isSelected ? 'primary' : 'text'}
                              onClick={() => handleRelationSelect(option.id)}
                            >
                              {option.label}
                            </Button>
                          );
                        })
                      ) : (
                        <div className={styles.relationPopoverEmpty}>
                          <FormattedMessage
                            id="family_tree_modal_relation_search_no_results"
                            defaultMessage="No matching relations"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                }
                open={seeMoreOpen}
                onOpenChange={(value) => {
                  if (!value && keepRelationPopoverOpenRef.current) {
                    keepRelationPopoverOpenRef.current = false;
                    return;
                  }

                  keepRelationPopoverOpenRef.current = false;

                  if (value) {
                    setSeeMoreOpen(true);
                    return;
                  }
                  setSeeMoreOpen(false);
                  setRelationSearchTerm('');
                }}
                trigger="click"
                placement="bottomLeft"
                zIndex={1200}
                destroyOnHidden
                closeOnBlur={false}
              >
                <Button
                  ref={seeMoreButtonRef}
                  // shape="round"
                  size={relationButtonSize}
                  type="link"
                  className={styles.relationMoreButton}
                  disabled={showGuestRegister && registrationComplete}
                  onClick={() => {
                    setSeeMoreOpen((prev) => {
                      const next = !prev;
                      if (next) {
                        setRelationSearchTerm('');
                      }
                      return next;
                    });
                  }}
                >
                  <FormattedMessage
                    id="family_tree_modal_more_relations"
                    defaultMessage="More options"
                  />
                </Button>
              </Popover>
            )}
          </div>

          {relation && (
            <div className={styles.relationReciprocalSection}>
            <span className={styles.label}>
              <FormattedMessage
                id="family_tree_modal_reciprocal_label"
                defaultMessage="How are they related to you?"
                values={{ name: resolvedPersonFirstName }}
              />
            </span>
              <div className={styles.relationOptionList}>
                {filteredDirections.map((direction) => {
                  const isSelected = relationDirection?.id === direction.id;
                  return (
                    <Button
                      shape="round"
                      key={direction.id}
                      size="small"
                      className={styles.relationChip}
                      type={isSelected ? 'primary' : 'default'}
                      disabled={showGuestRegister && registrationComplete}
                      onClick={() => handleRelationDirectionSelect(direction.id)}
                    >
                      {direction.label || formatRelationLabel(direction)}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        )}

        {activeAction === 'claim' && claimAvailable && (
          <div className={styles.claimSection}>
            <div className={styles.guardianInfo}>
              <div className={styles.guardianInfoTitle}>
                <FormattedMessage
                  id="claim_guardian_info_title"
                  defaultMessage="Hva innebærer omsorgsansvar?"
                />
              </div>
              <div className={styles.guardianInfoLead}>
                <FormattedMessage
                  id="claim_guardian_info_lead"
                  defaultMessage="Å være verge betyr å ivareta graven og fellesskapet rundt den."
                />
              </div>
              <ul className={styles.guardianInfoList}>
                <li>
                  <FormattedMessage
                    id="claim_guardian_info_bullet_relations"
                    defaultMessage="Godkjenne eller avvise relasjoner"
                  />
                </li>
                <li>
                  <FormattedMessage
                    id="claim_guardian_info_bullet_content"
                    defaultMessage="Administrere innhold på graven"
                  />
                </li>
                <li>
                  <FormattedMessage
                    id="claim_guardian_info_bullet_admins"
                    defaultMessage="Legge til eller fjerne administratorer"
                  />
                </li>
              </ul>
              <div className={styles.guardianInfoNote}>
                <FormattedMessage
                  id="claim_guardian_info_note"
                  defaultMessage="Omsorgsansvar bekreftes med dokumentasjon."
                />
              </div>
            </div>
            <span className={styles.subLabel}>
              <FormattedMessage
                id="claim_grave_message"
                defaultMessage="Would you like to be the owner of this grave?"
              />
            </span>
            <SelectDocument
              handelSelectedDocument={handleClaimDocumentTypeChange}
              selectedDocument={selectedDocumentType}
            />
            <div className={styles.subLabel}>
              <FormattedMessage
                id="claim_grave_document_helper"
                defaultMessage="This is only used to confirm responsibility for the grave."
              />
            </div>

            {selectedDocumentType && (
              <>
                <MediaUpload
                  accept=".doc,.pdf,.docx,.odt,.xls,.xlsx,.ods,.txt,.jpg,.png,.jpeg"
                  mediaType="document"
                  multiple={false}
                  value={documentItems}
                  allowDuplicates={false}
                  onChange={(items) => {
                    const nextItems = Array.isArray(items) ? items.slice(0, 1) : [];
                    setDocumentItems(nextItems);
                    if (nextItems.length === 0) {
                      setAcceptedTerms(false);
                    }
                  }}
                  onRemove={() => {
                    setDocumentItems([]);
                    setAcceptedTerms(false);
                  }}
                  strings={{
                    dropHint: <FormattedMessage id="grave.new.docs.drop_hint" />,
                  }}
                  renderTrigger={({ triggerOpen }) => (
                    <Button
                      variant="filled"
                      color="var(--surface-3)"
                      size="small"
                      shape="round"
                      style={{ color: 'var(--text)' }}
                      leftIcon={<Paperclip size={16} aria-hidden />}
                      onClick={(event) => {
                        event.preventDefault();
                        triggerOpen();
                      }}
                    >
                      <FormattedMessage id="grave.new.docs.button" />
                    </Button>
                  )}
                />
                {documentItems[0]?.file && (
                  <div className={styles.claimDocumentPreview}>{documentItems[0].file.name}</div>
                )}
                <TermsAndCondition
                  user={currentUser}
                  isCheckedTerms={setAcceptedTerms}
                  isChecked={acceptedTerms}
                />
              </>
            )}
          </div>
        )}
          </div>
          {showGuestRegister && (
            <div className={styles.splitAside}>
              <div className={styles.registerHeader}>
                {registrationComplete ? (
                  <FormattedMessage
                    id="signup_step_title"
                    defaultMessage="Confirm your email"
                  />
                ) : (
                  <FormattedMessage id="auth_register" defaultMessage="Create account" />
                )}
              </div>
              {!registrationComplete && (
                <div className={styles.registerLoginLink}>
                  <Button
                    type="link"
                    onClick={() => {
                      if (typeof window !== 'undefined') {
                        window.location.href = `/sign-in?next=${encodeURIComponent(
                          returnUrl,
                        )}`;
                      }
                    }}
                  >
                    <FormattedMessage id="signup_login_inline" />
                  </Button>
                </div>
              )}
              {registrationComplete ? (
                <div className={styles.registerSuccess}>
                  <div className={styles.registerSuccessTitle}>
                    <FormattedMessage id="signup_message_heading" />
                  </div>
                  <div className={styles.registerSuccessHighlight}>
                    <div className={styles.registerSuccessLabel}>
                      <FormattedMessage id="signup_primary_cta" />
                    </div>
                    <div className={styles.registerSuccessEmail}>{registeredUser?.email || '—'}</div>
                  </div>
                  <div className={styles.registerSuccessText}>
                    <FormattedMessage id="signup_secondary_cta_relation" />
                  </div>
                  <div className={styles.registerSuccessHelpText}>
                    <FormattedMessage id="signup_help_text" />
                  </div>
                  <div className={styles.registerActions}>
                    <Button
                      type="link"
                      onClick={handleResendConfirmation}
                      disabled={isResending}
                    >
                      <FormattedMessage id="signup_resend_action" />
                    </Button>
                    {!editEmailOpen && (
                      <>
                        <span className={styles.registerActionsDivider}>·</span>
                        <Button
                          type="link"
                          onClick={() => setEditEmailOpen(true)}
                          disabled={isResending}
                        >
                          <FormattedMessage id="signup_change_email" />
                        </Button>
                      </>
                    )}
                  </div>
                  {editEmailOpen && (
                    <div className={styles.registerEmailEdit}>
                      <Input
                        value={editEmailValue}
                        onChange={(event) => setEditEmailValue(event.target.value)}
                        placeholder={intl.formatMessage({ id: 'account_form_email_placeholder' })}
                      />
                      <Button
                        size="small"
                        onClick={handleUpdateEmail}
                        disabled={isResending || !editEmailValue.trim()}
                      >
                        <FormattedMessage id="signup_save_email" />
                      </Button>
                      <Button
                        size="small"
                        type="link"
                        onClick={() => {
                          setEditEmailOpen(false);
                          setEditEmailValue(registeredUser?.email || '');
                        }}
                        disabled={isResending}
                      >
                        <FormattedMessage id="signup_cancel_email" />
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <InputForm
                  setHidePage={setRegistrationComplete}
                  setUser={setRegisteredUser}
                  onSuccess={(createdUser) => {
                    if (createdUser?.full_name || createdUser?.email) {
                      setRegisteredUser({
                        name: createdUser.full_name,
                        email: createdUser.email,
                        id: createdUser.id,
                        slug: createdUser.slug,
                        confirmation_email_status: createdUser.confirmation_email_status,
                        confirmation_email_error_code: createdUser.confirmation_email_error_code,
                        confirmation_email_error_message: createdUser.confirmation_email_error_message,
                        confirmation_email_sent_at: createdUser.confirmation_email_sent_at,
                        confirmation_email_last_event_at: createdUser.confirmation_email_last_event_at,
                      });
                    }
                  }}
                  onValuesChange={(_, allValues) => {
                    if (allValues?.gender != null) {
                      setGuestGender(allValues.gender);
                    }
                  }}
                  isJoinSheet
                  hideLocaleFields
                  defaultCountryCode={guestDefaults.country}
                  defaultLanguageKey={guestDefaults.language}
                  hideSubmitButton
                  onSubmitReady={(submit) => {
                    registerSubmitRef.current = submit;
                  }}
                  onSubmitStateChange={setIsRegistering}
                />
              )}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default SetRelationshipModal;
